import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Box, ClickAwayListener, Paper, Popper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { OverflowChipsContainer } from './OverflowChipsContainer';
import { SearchInput } from './SearchInput';
import { FilterFieldsList } from './FilterFieldsList';
import { FilterOptionsList } from './FilterOptionsList';
import { useInsightsFilterState } from './useInsightsFilterState';
import { useFilterData, useBuildFilterPills } from './useFilterData';
import { useServerOptions } from './useServerOptions';
import { useSearchBarHandlers } from './useSearchBarHandlers';
import { filterFieldsBySearchTerm } from './utils';
import type { FilterPill } from './InsightsSearchBar.types';
import { abyssColors } from '@/theme/colors';

export const InsightsSearchBar = () => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const searchBarInnerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeFieldName, setActiveFieldName] = useState<string | null>(null);
  const [editingPill, setEditingPill] = useState<FilterPill | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const structureId = useGetStructureId();

  const {
    selectedTemplateFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    activeFieldId,
    setActiveFieldId,
    removePill,
    clearAllFilters,
    addTemplateOption,
    addDeckFilter,
    addPaintRegionFilter,
    addWorkpackFilter,
    setApplyFilters,
    searchTerm,
    setSearchTerm,
    rangeSearchTerm,
    setRangeSearchTerm,
  } = useInsightsFilterState();

  const {
    filterFields,
    decksData,
    paintRegionsData,
    workpacksData,
    assemblyTemplate,
    unitSystem,
    structurePipes,
  } = useFilterData(structureId);

  const { inputValue, setInputValue, getDisplayOptions, isFieldLoading } = useServerOptions({
    activeFieldId,
    filterFields,
    assemblyTemplate,
    unitSystem,
    structurePipes,
  });

  const { closeDropdown, handleSelectOption, handleSearchSubmit, handleSaveEditedPill } =
    useSearchBarHandlers({
      filterFields,
      assemblyTemplate,
      decksData,
      paintRegionsData,
      workpacksData,
      activeFieldId,
      setActiveFieldId,
      addTemplateOption,
      addDeckFilter,
      addPaintRegionFilter,
      addWorkpackFilter,
      setApplyFilters,
      setSearchTerm,
      setRangeSearchTerm,
      removePill,
    });

  const filterPills = useBuildFilterPills(
    selectedTemplateFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    assemblyTemplate,
    decksData,
    paintRegionsData,
    workpacksData,
    unitSystem,
    structurePipes,
  );

  const allPills = useMemo(() => {
    const pills = [...filterPills];

    if (searchTerm) {
      const terms = searchTerm.split('||').filter((t) => t.trim());
      terms.forEach((term) => {
        pills.push({
          fieldName: 'Search',
          fieldKey: 'searchTerm',
          value: term,
          displayValue: term,
          type: 'searchTerm',
        });
      });
    }

    if (rangeSearchTerm?.trim()) {
      pills.push({
        fieldName: 'Range Filter',
        fieldKey: 'rangeSearchTerm',
        value: rangeSearchTerm,
        displayValue: rangeSearchTerm,
        type: 'rangeSearchTerm',
      });
    }

    return pills;
  }, [filterPills, searchTerm, rangeSearchTerm]);

  const activeField = filterFields.find((f) => f.id === activeFieldId);
  const displayOptions = getDisplayOptions(activeField);
  const filteredFields = filterFieldsBySearchTerm(filterFields, inputValue);
  const hasFilters = allPills.length > 0;

  const doCloseDropdown = useCallback(() => {
    closeDropdown(setActiveFieldName, setInputValue, setIsOpen);
  }, [closeDropdown, setInputValue]);

  useEffect((): (() => void) | void => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        doCloseDropdown();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, doCloseDropdown]);

  const handleFieldSelect = useCallback((fieldId: string, fieldName: string): void => {
    setActiveFieldId((prev) => (prev === fieldId ? null : fieldId));
    setActiveFieldName(fieldName);
    setInputValue('');
    setIsOpen(true);
  }, [setActiveFieldId, setInputValue]);

  const handleOptionClick = useCallback((optionId: string): void => {
    handleSelectOption(activeField, optionId, doCloseDropdown);
  }, [activeField, handleSelectOption, doCloseDropdown]);

  const handleEnterPress = useCallback((value: string): void => {
    handleSearchSubmit(value, doCloseDropdown);
  }, [handleSearchSubmit, doCloseDropdown]);

  const handleEditPill = useCallback((pill: FilterPill): void => {
    setEditingPill(pill);
    const displayVal =
      pill.type === 'deck' || pill.type === 'paintRegion' || pill.type === 'workpack'
        ? (pill.displayValue ?? pill.value)
        : pill.value;
    setEditingValue(`${pill.fieldName}:${displayVal}`);
    setIsOpen(false);
  }, []);

  const handleCancelEdit = useCallback((): void => {
    setEditingPill(null);
    setEditingValue('');
  }, []);

  const handleSavePill = useCallback((pill: FilterPill, value: string): void => {
    handleSaveEditedPill(pill, value, () => {
      setEditingPill(null);
      setEditingValue('');
      doCloseDropdown();
    });
  }, [handleSaveEditedPill, doCloseDropdown]);

  const handleClickAway = useCallback((event: MouseEvent | TouchEvent): void => {
    const target = event.target as Node | null;
    if (anchorRef.current?.contains(target)) return;
    doCloseDropdown();
  }, [doCloseDropdown]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        doCloseDropdown();
        inputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, doCloseDropdown]);

  return (
    <Box
      sx={{ position: 'relative', width: '100%', mt: 1.5 }}
      ref={anchorRef}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
    >
      <Box
        ref={searchBarInnerRef}
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 0.5,
          border: `1px solid ${abyssColors.primary[200]}`,
          borderRadius: '0.25rem',
          padding: '0.5rem',
          minHeight: '3rem',
          backgroundColor: '#fff',
          cursor: 'text',
          overflow: 'hidden',
        }}
        role="search"
        aria-label="Insights filters and search"
        onClick={() => {
          inputRef.current?.focus();
          setIsOpen(true);
        }}
      >
        <OverflowChipsContainer
          pills={allPills}
          onRemovePill={removePill}
          onEditPill={handleEditPill}
          editingPill={editingPill}
          editingValue={editingValue}
          onChangeEditingValue={setEditingValue}
          onSaveEditedPill={handleSavePill}
          onCancelEdit={handleCancelEdit}
          searchInputRef={inputRef}
          searchBarRef={searchBarInnerRef}
        />

        <SearchInput
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onClearActiveField={doCloseDropdown}
          activeFieldName={activeFieldName}
          hasFilters={hasFilters}
          onEnterPress={handleEnterPress}
          onCancelEdit={handleCancelEdit}
          aria-label="Insights search input"
        />

        {hasFilters && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              clearAllFilters();
            }}
            aria-label="Clear all filters"
            sx={{
              padding: '4px',
              marginLeft: 'auto',
              color: abyssColors.primary[300],
              '&:hover': { color: abyssColors.primary[500] },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1_300, width: anchorRef.current?.offsetWidth, maxWidth: '90vw' }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            sx={{
              p: 1,
              border: `1px solid ${abyssColors.primary[200]}`,
              borderRadius: '4px',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {activeFieldId ? (
              <FilterOptionsList
                fieldName={activeFieldName || 'Options'}
                options={displayOptions}
                isLoading={isFieldLoading}
                onOptionSelect={handleOptionClick}
              />
            ) : (
              <FilterFieldsList fields={filteredFields} onFieldSelect={handleFieldSelect} />
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

